import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { api } from '@/utils/api';
import { EVENTO_SESION_VENCIDA, EVENTO_SESION_RENOVADA, hayQueRenovar, estaVencido } from '@/utils/sesion';

const AuthContext = createContext(undefined);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  // La sesión se venció en medio del uso: ModalSesionVencida pide la clave.
  const [sesionVencida, setSesionVencida] = useState(false);
  const vencida = useRef(false);
  // Mientras se abre la app no hay nada sin guardar: una sesión vencida ahí
  // no abre la ventanita, se cierra y se va al login.
  const iniciando = useRef(true);

  const marcarVencida = (valor) => {
    vencida.current = valor;
    setSesionVencida(valor);
  };

  // Al volver a entrar, la pantalla que estaba guardando reintenta sola.
  const avisarRenovada = () => {
    if (!vencida.current) return;
    marcarVencida(false);
    window.dispatchEvent(new CustomEvent(EVENTO_SESION_RENOVADA));
  };

  // La sesión dura 30 días desde que se emite. Renovarla al usar la app hace
  // que venza solo tras 30 días SIN usarla, no a los 30 del último login.
  const renovarSiHaceFalta = useCallback(async () => {
    const actual = localStorage.getItem('auth_token');
    if (!hayQueRenovar(actual)) return;
    try {
      const r = await api.renovarSesion();
      if (r?.access_token) {
        localStorage.setItem('auth_token', r.access_token);
        setToken(r.access_token);
      }
    } catch (_e) {
      // Si la sesión ya venció, api.js avisa y se abre la ventanita.
    }
  }, []);

  const loadStoredAuth = useCallback(async () => {
    try {
      const storedToken = localStorage.getItem('auth_token');
      const storedUser = localStorage.getItem('auth_user');

      if (storedToken && estaVencido(storedToken)) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        return;
      }

      if (storedToken && storedUser) {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
        
        try {
          const freshUser = await api.getMe(storedToken);
          if (freshUser) {
            setUser(freshUser);
            localStorage.setItem('auth_user', JSON.stringify(freshUser));
          }
        } catch (e) {
          if (e?.status === 401) {
            // Sesión inválida (por ejemplo, cambió la clave del servidor):
            // recién se abre la app, no hay nada que conservar.
            localStorage.removeItem('auth_token');
            localStorage.removeItem('auth_user');
            setToken(null);
            setUser(null);
            return;
          }
          console.log('Could not refresh user data');
        }
        await renovarSiHaceFalta();
      }
    } catch (error) {
      console.error('Error loading auth:', error);
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
    } finally {
      iniciando.current = false;
      setLoading(false);
    }
  }, [renovarSiHaceFalta]);

  useEffect(() => {
    loadStoredAuth();
  }, [loadStoredAuth]);

  useEffect(() => {
    const alVencer = () => {
      if (!iniciando.current && localStorage.getItem('auth_user')) marcarVencida(true);
    };
    // Entró en otra pestaña (por ejemplo, con código por mail): esta sigue.
    const alCambiarEnOtraPestana = (e) => {
      if (e.key !== 'auth_token' || !e.newValue) return;
      setToken(e.newValue);
      avisarRenovada();
    };
    // Volver a la pestaña después de horas cuenta como uso.
    const alVolver = () => {
      if (document.visibilityState === 'visible') renovarSiHaceFalta();
    };
    window.addEventListener(EVENTO_SESION_VENCIDA, alVencer);
    window.addEventListener('storage', alCambiarEnOtraPestana);
    document.addEventListener('visibilitychange', alVolver);
    return () => {
      window.removeEventListener(EVENTO_SESION_VENCIDA, alVencer);
      window.removeEventListener('storage', alCambiarEnOtraPestana);
      document.removeEventListener('visibilitychange', alVolver);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renovarSiHaceFalta]);

  const login = async (email, password) => {
    const data = await api.login(email, password);
    localStorage.setItem('auth_token', data.access_token);
    localStorage.setItem('auth_user', JSON.stringify(data.user));
    setToken(data.access_token);
    setUser(data.user);
    avisarRenovada();
    return data;
  };

  // Desde la ventanita de sesión vencida: misma cuenta, solo la contraseña.
  const reingresar = async (password) => login(user.email, password);

  // Setter genérico para flujos que ya obtuvieron el token por su cuenta (ej: OTP).
  const setSession = ({ token: t, user: u }) => {
    if (!t || !u) return;
    localStorage.setItem('auth_token', t);
    localStorage.setItem('auth_user', JSON.stringify(u));
    setToken(t);
    setUser(u);
  };

  const register = async (email, password, name) => {
    const data = await api.register(email, password, name);
    localStorage.setItem('auth_token', data.access_token);
    localStorage.setItem('auth_user', JSON.stringify(data.user));
    setToken(data.access_token);
    setUser(data.user);
    return data;
  };

  const logout = async () => {
    marcarVencida(false);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const freshUser = await api.getMe();
      if (freshUser) {
        setUser(freshUser);
        localStorage.setItem('auth_user', JSON.stringify(freshUser));
      }
    } catch (e) {
      console.log('Could not refresh user');
    }
  };

  const isFullUser = user?.account_type === 'full';
  const isTrialUser = user?.account_type === 'trial';

  return (
    <AuthContext.Provider value={{ 
      user, token, loading, 
      login, register, logout, setSession, refreshUser,
      sesionVencida, reingresar,
      isFullUser, isTrialUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
